import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { type ClientLoaderFunctionArgs, useLoaderData, useOutletContext, useRouteLoaderData } from '@remix-run/react';

import FormLayout from '~/pages/forms/FormLayout';

import * as Layout from '~/components/Layout';

import type { OutletContext } from '~/interfaces/outlet_context';

import getForm from '~/services/api/forms/getForm.server';
import getPublication from '~/services/api/publication/getPublication.server';

import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';

import remixI18n from '~/i18next.server';
import { loaderSession } from '~/session';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const metaTitle = data?.metaTitle;

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      title: metaTitle,
      robots: 'noindex',
    },
  });
};

export const loader: LoaderFunction = async ({ request, params }) => {
  const { session } = await loaderSession(request);
  const { formId } = params;

  const [publication, form] = await Promise.all([
    getPublication(request, session),
    getForm(request, session, formId || ''),
  ]);
  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  // Redirect when preview mode is invalid
  const url = new URL(request.url);
  const isPreview = url.searchParams.get('preview') === 'true';

  const hasNoQuestions = !form.form_questions.length;
  const isNotInDraftState = form.status !== 'draft';
  const shouldRedirect = isPreview && (hasNoQuestions || isNotInDraftState);
  if (shouldRedirect) {
    return redirect('/');
  }

  return Response.json({
    requestUrl: request.url,
    isPreview,
    defaultEmail: '',
    publication,
    form,
    metaTitle: t('meta.titles.forms'),
  });
};

const parseEmail = (email: string | null): string => {
  try {
    if (!email) {
      return '';
    }

    return JSON.parse(email);
  } catch (_e) {
    return email || '';
  }
};

const isReferredFromTrustedOrigin = (request: ClientLoaderFunctionArgs['request']) => {
  try {
    const refererInHeader = request.headers.get('Referer');
    const url = new URL(request.url);
    const refererInParams = url.searchParams.get('_bhref');

    return refererInHeader?.includes('subscribe-forms.beehiiv.com') || refererInParams === 'subscribe-forms';
  } catch {
    return false;
  }
};

export const clientLoader = async ({ serverLoader, request }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<typeof loader>();
  const emailInLocalStorage = parseEmail(localStorage.getItem('email'));

  let emailInParams = '';
  try {
    const url = new URL(request.url);
    emailInParams = isReferredFromTrustedOrigin(request) ? url.searchParams.get('email') || '' : '';
  } catch {
    // do nothing
  }

  return {
    ...serverData,
    defaultEmail: emailInLocalStorage || emailInParams,
  };
};

clientLoader.hydrate = true;

const PublicationForm = () => {
  const { publication, form, defaultEmail, isPreview } = useLoaderData<typeof loader>();
  const { subscription } = useRouteLoaderData<typeof loader>('root');
  const email = subscription?.email || defaultEmail;

  const { cookies, cookiesSet } = useOutletContext<OutletContext>();
  const disablePreviewNoteCookieExists = cookiesSet && Boolean(cookies.form_preview_banner_disabled);

  return (
    <Layout.Page className="px-6 pb-10">
      <Layout.Main>
        <FormLayout
          email={email}
          publication={publication}
          form={form}
          isPreview={isPreview}
          disablePreviewNoteCookieExists={disablePreviewNoteCookieExists}
        />
      </Layout.Main>
    </Layout.Page>
  );
};

export default PublicationForm;
